import { useState } from 'react';
import { HistoryOutlined, RollbackOutlined, SaveOutlined, SearchOutlined, UndoOutlined } from '@ant-design/icons';

import { DataTable } from '../../../../components/shared';
import { Badge, Button, Checkbox, Dialog, Empty, Input, InputPassword, Select, Skeleton } from '../../../../components/ui';
import { useLoi } from '../../../../hooks/useLoi';
import { useT } from '../../../../i18n';
import type { KhoaDich } from '../../../../i18n';
import { KHONG_CO, ngayGioVN, soVN } from '../../../../lib/dinh-dang';
import { maLoi } from '../../../../lib/errors';
import { useToast } from '../../../../lib/toast';
import { LoiTrongHop } from '../../../tenant/components/phan-form';
import { useTenNguoi } from '../../../xu-ly/hooks/useTenNguoi';
import { BADGE_DA_SUA, LOAI_BAN_LUU, LOAI_GIA_TRI } from '../../constants';
import { useGiaTri, useKhoiPhucGiaTri, useLichSuGiaTri, useLuuGiaTri } from '../../hooks/useOpenCore';
import { coLoiGiaTri, locGiaTri, loiGiaTri, mucDaDoi } from '../../nghiep-vu/gia-tri';
import type { BanNhapGiaTri, BoLocGiaTri, LocLoaiGiaTri, LoiGiaTri } from '../../nghiep-vu/gia-tri';
import type { BanLuuGiaTri, GiaTriWorkflow, MucGiaTri } from '../../types';
import { LoiRpa } from '../chung';

const AN = '••••••';
const DAI_TOI_DA = 4000;

const HUONG: Record<string, KhoaDich> = {
  In: 'rpaGiaTri.huongIn',
  Out: 'rpaGiaTri.huongOut',
  InOut: 'rpaGiaTri.huongInOut',
  Property: 'rpaGiaTri.huongProperty',
};

const LOI: Record<LoiGiaTri, KhoaDich> = {
  boolean: 'rpaGiaTri.loiBoolean',
  integer: 'rpaGiaTri.loiInteger',
  number: 'rpaGiaTri.loiNumber',
};

const LY_DO: Record<string, KhoaDich> = {
  EXPRESSION: 'rpaGiaTri.lyDoEXPRESSION',
  TYPE: 'rpaGiaTri.lyDoTYPE',
  OUT_ARGUMENT: 'rpaGiaTri.lyDoOUT_ARGUMENT',
  COMPLEX: 'rpaGiaTri.lyDoCOMPLEX',
};

/**
 * Tab "Biến & tham số" (`WorkflowValuesPanel` của native): xem giá trị mặc định của tham số và biến; người có
 * `rpa:workflow:update` sửa được giá trị chữ, số, True/False rồi ghi lại Workflow trên OpenCore, xem lịch sử và khôi
 * phục. Bản nháp và bộ lọc do panel cha giữ, nên đổi sang tab khác rồi quay lại không mất phần đang sửa.
 */
export function TabGiaTri({
  workflowId,
  ten,
  suaDuoc,
  banNhap,
  setBanNhap,
  loc,
  setLoc,
}: {
  workflowId: string;
  ten: string;
  suaDuoc: boolean;
  banNhap: BanNhapGiaTri;
  setBanNhap: (f: (cu: BanNhapGiaTri) => BanNhapGiaTri) => void;
  loc: BoLocGiaTri;
  setLoc: (loc: BoLocGiaTri) => void;
}) {
  const { t, locale } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const q = useGiaTri(workflowId);
  const luu = useLuuGiaTri(workflowId);
  const [xacNhan, setXacNhan] = useState(false);
  const [moLichSu, setMoLichSu] = useState(false);

  if (q.isPending) return <Skeleton className="h-48 w-full" />;
  if (q.isError) return <LoiRpa loi={q.error} onThuLai={() => void q.refetch()} />;

  const du = q.data;
  const doi = mucDaDoi(du.items, banNhap);
  const coLoi = coLoiGiaTri(du.items, banNhap);
  const dong = locGiaTri(du.items, loc);

  const gui = () =>
    luu.mutate(
      { baseVersion: du.version, changes: doi.map(({ muc, giaTri }) => ({ key: muc.key, value: giaTri })) },
      {
        onSuccess: (kq) => {
          setBanNhap(() => ({}));
          setXacNhan(false);
          toast.success(t('rpaGiaTri.daLuu', { so: soVN(kq.applied.length, locale) }));
        },
        onError: (e) => {
          // Workflow vừa bị sửa ở Designer hay bởi người khác: tải bản mới để người dùng sửa lại trên đó.
          if (maLoi(e) === 'RPA_WORKFLOW_CHANGED') {
            setXacNhan(false);
            toast.error(dichLoi(e));
            void q.refetch();
          }
        },
      },
    );

  return (
    <div className="min-w-0 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          allowClear
          className="w-full sm:w-56"
          prefix={<SearchOutlined aria-hidden />}
          placeholder={t('rpaGiaTri.tim')}
          aria-label={t('rpaGiaTri.tim')}
          value={loc.tim}
          onChange={(e) => setLoc({ ...loc, tim: e.target.value })}
        />
        <Select<LocLoaiGiaTri>
          className="w-40"
          value={loc.loai}
          aria-label={t('rpaGiaTri.locLoai')}
          onChange={(loai) => setLoc({ ...loc, loai })}
          options={[
            { value: '', label: t('rpaGiaTri.tatCaLoai') },
            { value: 'argument', label: t('rpaGiaTri.thamSo') },
            { value: 'variable', label: t('rpaGiaTri.bien') },
          ]}
        />
        <Checkbox checked={loc.chiCoGiaTri} onChange={(e) => setLoc({ ...loc, chiCoGiaTri: e.target.checked })}>
          {t('rpaGiaTri.chiCoGiaTri')}
        </Checkbox>
        {suaDuoc && (
          <div className="ml-auto flex flex-wrap gap-2">
            <Button variant="outline" size="sm" icon={<HistoryOutlined />} onClick={() => setMoLichSu(true)}>
              {t('rpaGiaTri.lichSu')}
            </Button>
            <Button variant="outline" size="sm" icon={<UndoOutlined />} disabled={doi.length === 0} onClick={() => setBanNhap(() => ({}))}>
              {t('rpaGiaTri.hoanTac')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              icon={<SaveOutlined />}
              disabled={doi.length === 0 || coLoi}
              onClick={() => {
                luu.reset();
                setXacNhan(true);
              }}
            >
              {t('rpaGiaTri.luuSo', { so: soVN(doi.length, locale) })}
            </Button>
          </div>
        )}
      </div>

      {dong.length === 0 ? (
        // Có mục mà lọc hết: rỗng vì bộ lọc, không mô tả.
        <Empty title={t(du.items.length > 0 ? 'rpaGiaTri.rongLoc' : 'rpaGiaTri.rong')} className="py-6" />
      ) : (
        <DataTable<MucGiaTri>
          caption={t('rpaGiaTri.caption', { ten })}
          size="small"
          rowKey="key"
          pagination={false}
          dataSource={dong}
          columns={[
            { title: t('chung.stt'), key: 'stt', width: 52, render: (_, _m, i) => <span className="tabular-nums">{soVN(i + 1, locale)}</span> },
            {
              title: t('rpaGiaTri.cotTen'),
              key: 'name',
              width: 220,
              render: (_, m) => (
                <div className="min-w-0">
                  <span className="break-all font-medium text-content-primary">{m.name}</span>
                  {doi.some((c) => c.muc.key === m.key) && (
                    <Badge variant={BADGE_DA_SUA} className="ml-1.5">{t('rpaGiaTri.daSua')}</Badge>
                  )}
                  {m.scope && <span className="block truncate text-xs text-content-muted">{t('rpaGiaTri.trong', { pham: m.scope })}</span>}
                </div>
              ),
            },
            {
              title: t('rpaGiaTri.cotLoai'),
              key: 'kind',
              width: 130,
              render: (_, m) => (
                <Badge variant={LOAI_GIA_TRI[m.kind]} className="whitespace-nowrap">
                  {m.kind === 'argument' ? t(HUONG[m.direction] ?? 'rpaGiaTri.huongIn') : t('rpaGiaTri.bien')}
                </Badge>
              ),
            },
            { title: t('rpaGiaTri.cotKieu'), key: 'type', width: 110, render: (_, m) => <code className="break-all text-xs">{m.type}</code> },
            {
              title: t('rpaGiaTri.cotGiaTri'),
              key: 'value',
              render: (_, m) => (
                <OGiaTri muc={m} nhap={banNhap[m.key]} suaDuoc={suaDuoc} onDoi={(v) => setBanNhap((cu) => ({ ...cu, [m.key]: v }))} />
              ),
            },
          ]}
        />
      )}

      {xacNhan && (
        <Dialog
          open
          onClose={() => setXacNhan(false)}
          width={560}
          title={t('rpaGiaTri.xacNhanTieuDe', { so: soVN(doi.length, locale) })}
          footer={
            <div className="space-y-3">
              {luu.error && maLoi(luu.error) !== 'RPA_WORKFLOW_CHANGED' && <LoiTrongHop>{dichLoi(luu.error)}</LoiTrongHop>}
              <div className="flex flex-wrap justify-end gap-3">
                <Button variant="outline" size="md" onClick={() => setXacNhan(false)}>
                  {t('chung.huy')}
                </Button>
                <Button variant="primary" size="md" loading={luu.isPending} onClick={gui}>
                  {luu.isPending ? t('chung.dangLuu') : t('rpaGiaTri.luuVao')}
                </Button>
              </div>
            </div>
          }
        >
          <p className="mb-3 text-sm text-content-secondary">{t('rpaGiaTri.xacNhanThan')}</p>
          <ul className="space-y-1.5 text-sm">
            {doi.map(({ muc, giaTri }) => (
              <li key={muc.key} className="rounded bg-subtle px-3 py-2">
                <span className="font-medium text-content-primary">{muc.name}</span>
                <span className="block break-all text-xs text-content-secondary">
                  {muc.secret ? t('rpaGiaTri.biMatMoi') : `${muc.value || t('rpaGiaTri.khongDat')} → ${giaTri || t('rpaGiaTri.khongDat')}`}
                </span>
              </li>
            ))}
          </ul>
        </Dialog>
      )}

      {moLichSu && (
        <HopLichSu workflowId={workflowId} giaTri={du} onClose={() => setMoLichSu(false)} onDaKhoiPhuc={() => setBanNhap(() => ({}))} />
      )}
    </div>
  );
}

/**
 * Ô giá trị của một mục: không sửa được thì hiện giá trị kèm lý do; người chỉ xem thấy giá trị (bí mật che lại); bí
 * mật là ô mật khẩu (trống = giữ); True/False là ô chọn; còn lại là ô chữ, báo sai kiểu ngay dưới ô.
 */
function OGiaTri({ muc, nhap, suaDuoc, onDoi }: { muc: MucGiaTri; nhap: string | undefined; suaDuoc: boolean; onDoi: (v: string) => void }) {
  const { t } = useT();
  if (!muc.editable) {
    return (
      <div className="min-w-0 text-xs">
        <code className="block truncate text-content-secondary" title={muc.value}>{muc.value || KHONG_CO}</code>
        <span className="text-content-muted">{t(LY_DO[muc.reason] ?? 'rpaGiaTri.lyDoTYPE')}</span>
      </div>
    );
  }
  if (!suaDuoc) {
    const hien = muc.secret ? (muc.hasValue ? AN : '') : muc.value;
    return <code className="block truncate text-xs" title={hien}>{hien || KHONG_CO}</code>;
  }
  const giaTri = nhap ?? (muc.secret ? '' : muc.value);
  if (muc.secret) {
    return (
      <InputPassword
        size="small"
        autoComplete="new-password"
        value={giaTri}
        aria-label={muc.name}
        placeholder={t(muc.hasValue ? 'rpaGiaTri.biMatGiu' : 'rpaGiaTri.biMatTrong')}
        onChange={(e) => onDoi(e.target.value)}
      />
    );
  }
  if (muc.valueType === 'boolean') {
    return (
      <Select<string>
        size="small"
        className="w-full"
        value={giaTri}
        aria-label={muc.name}
        onChange={onDoi}
        options={[
          { value: '', label: t('rpaGiaTri.khongDat') },
          { value: 'True', label: 'True' },
          { value: 'False', label: 'False' },
        ]}
      />
    );
  }
  const loi = loiGiaTri(muc, giaTri);
  return (
    <div>
      <Input
        size="small"
        value={giaTri}
        maxLength={DAI_TOI_DA}
        aria-label={muc.name}
        aria-invalid={loi !== null}
        status={loi ? 'error' : undefined}
        inputMode={muc.valueType === 'string' ? undefined : 'decimal'}
        placeholder={t('rpaGiaTri.khongDat')}
        onChange={(e) => onDoi(e.target.value)}
      />
      {loi && <p role="alert" className="mt-0.5 text-xs text-danger-700">{t(LOI[loi])}</p>}
    </div>
  );
}

/** Các lần sửa từ Native; khôi phục = đưa Workflow về đúng XAML trước lần sửa đó (bản hiện tại vẫn được lưu lại). */
function HopLichSu({
  workflowId,
  giaTri,
  onClose,
  onDaKhoiPhuc,
}: {
  workflowId: string;
  giaTri: GiaTriWorkflow;
  onClose: () => void;
  onDaKhoiPhuc: () => void;
}) {
  const { t, locale } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const tenNguoi = useTenNguoi();
  const ls = useLichSuGiaTri(workflowId, true);
  const khoiPhuc = useKhoiPhucGiaTri(workflowId);
  const [chon, setChon] = useState<BanLuuGiaTri | null>(null);

  const xacNhan = (b: BanLuuGiaTri) =>
    khoiPhuc.mutate(
      { backupId: b.id, baseVersion: giaTri.version },
      {
        onSuccess: () => {
          toast.success(t('rpaGiaTri.daKhoiPhuc'));
          onDaKhoiPhuc();
          onClose();
        },
      },
    );

  return (
    <Dialog
      open
      onClose={onClose}
      width={640}
      title={t('rpaGiaTri.lichSuTieuDe')}
      footer={
        chon ? (
          <div className="space-y-3">
            {khoiPhuc.error && <LoiTrongHop>{dichLoi(khoiPhuc.error)}</LoiTrongHop>}
            <div className="flex flex-wrap items-center justify-end gap-3">
              <p className="mr-auto min-w-0 text-sm text-content-secondary">
                {t('rpaGiaTri.khoiPhucHoi', { luc: ngayGioVN(chon.savedAt, locale) })}
              </p>
              <Button
                variant="outline"
                size="md"
                onClick={() => {
                  khoiPhuc.reset();
                  setChon(null);
                }}
              >
                {t('chung.huy')}
              </Button>
              <Button variant="destructive" size="md" loading={khoiPhuc.isPending} onClick={() => xacNhan(chon)}>
                {t('rpaGiaTri.khoiPhuc')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex justify-end">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.dong')}
            </Button>
          </div>
        )
      }
    >
      {ls.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : ls.isError ? (
        <LoiRpa loi={ls.error} onThuLai={() => void ls.refetch()} />
      ) : ls.data.length === 0 ? (
        <Empty icon={HistoryOutlined} title={t('rpaGiaTri.lichSuRong')} className="py-6" />
      ) : (
        <ul className="space-y-2">
          {ls.data.map((b) => {
            const loai = LOAI_BAN_LUU[b.action] ?? LOAI_BAN_LUU.edit;
            return (
              <li key={b.id} className={chon?.id === b.id ? 'rounded-lg border border-danger-500 p-3' : 'rounded-lg border border-default p-3'}>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={loai.variant}>{t(loai.khoa)}</Badge>
                  <span className="text-sm tabular-nums text-content-primary">{ngayGioVN(b.savedAt, locale)}</span>
                  <span className="text-xs text-content-muted">{tenNguoi(b.savedBy) ?? KHONG_CO}</span>
                  <Button variant="ghost" size="sm" className="ml-auto" icon={<RollbackOutlined />} onClick={() => setChon(b)}>
                    {t('rpaGiaTri.khoiPhucTruoc')}
                  </Button>
                </div>
                <ul className="mt-1 space-y-0.5 text-xs text-content-secondary">
                  {b.changes.map((c, i) =>
                    'restoredFrom' in c ? (
                      <li key={`kp-${i}`}>{t('rpaGiaTri.khoiPhucTu', { luc: ngayGioVN(c.savedAt, locale) })}</li>
                    ) : (
                      <li key={c.key} className="break-all">
                        <span className="font-medium text-content-primary">{c.name}</span>:{' '}
                        {c.secret ? t('rpaGiaTri.biMatMoi') : `${c.from || t('rpaGiaTri.khongDat')} → ${c.to || t('rpaGiaTri.khongDat')}`}
                      </li>
                    ),
                  )}
                </ul>
              </li>
            );
          })}
        </ul>
      )}
    </Dialog>
  );
}
